import { useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useTranslation } from "@/i18n/useTranslation";
import type { AvailableForm, Script, ScriptForm } from "@/features/dashboard/types";
import { calculateScriptFormScore } from "@/features/dashboard/utils";
import { VersionControlPanel } from "@/features/versions/components";
import { useVersionSelection } from "@/features/versions/context/VersionSelectionContext";
import { ScoreTableHeader } from "./ScoreTableHeader";
import { ScoreTableRow } from "./ScoreTableRow";
import type { ScriptRowMeta, VisibleForm } from "./types";

const MAX_FORM_COLUMNS = 4;
const DEFAULT_FORM_NAMES = [
  "Concept & Pitch",
  "Screenplay & Treatment",
  "Project Development & Financing",
];

interface ScriptScoreTableProps {
  scripts: Script[];
  formatDate: (dateString: string) => string;
  onShare: (script: Script, scriptForm: ScriptForm) => void;
  onAnalyze: (scriptForm: ScriptForm, scriptName: string, script: Script) => void;
  onAddForm: (script: Script, formId: string) => Promise<void>;
  availableFormsByScript: Record<string, AvailableForm[]>;
  addingForm: { scriptId: string; formId: string } | null;
  onRefresh?: () => Promise<void>;
}

export const ScriptScoreTable = ({
  scripts,
  formatDate,
  onShare,
  onAnalyze,
  onAddForm,
  availableFormsByScript,
  addingForm,
  onRefresh,
}: ScriptScoreTableProps) => {
  const { t, language } = useTranslation();
  const { getSelectedVersion, setSelectedVersion } = useVersionSelection();
  const [versionControlOpen, setVersionControlOpen] = useState<string | null>(null);
  const parentRef = useRef<HTMLDivElement | null>(null);

  const formColumns = useMemo<VisibleForm[]>(() => {
    const frequency = new Map<string, { name: string; nameTh: string | null; count: number }>();

    scripts.forEach((script) => {
      script.script_forms.forEach((scriptForm) => {
        const formId = scriptForm.feedback_forms.id;
        const name = scriptForm.feedback_forms.name;
        const nameTh = scriptForm.feedback_forms.name_th ?? null;
        const current = frequency.get(formId);
        if (current) {
          frequency.set(formId, { name, nameTh, count: current.count + 1 });
        } else {
          frequency.set(formId, { name, nameTh, count: 1 });
        }
      });
    });

    Object.values(availableFormsByScript).forEach((forms) => {
      forms.forEach((form) => {
        const existing = frequency.get(form.id);
        if (existing) {
          if (!existing.nameTh && form.name_th) {
            frequency.set(form.id, {
              name: existing.name,
              nameTh: form.name_th,
              count: existing.count,
            });
          }
          return;
        }

        frequency.set(form.id, {
          name: form.name,
          nameTh: form.name_th ?? null,
          count: 0,
        });
      });
    });

    const sortedEntries = Array.from(frequency.entries()).sort((a, b) => {
      if (b[1].count === a[1].count) {
        return a[1].name.localeCompare(b[1].name);
      }
      return b[1].count - a[1].count;
    });

    const prioritized: Array<[string, { name: string; nameTh: string | null; count: number }]> = [];
    const appended = new Set<string>();

    DEFAULT_FORM_NAMES.forEach((name) => {
      const match = sortedEntries.find(([, value]) => value.name === name);
      if (match && !appended.has(match[0])) {
        prioritized.push(match);
        appended.add(match[0]);
      }
    });

    for (const entry of sortedEntries) {
      if (prioritized.length >= MAX_FORM_COLUMNS) {
        break;
      }
      if (appended.has(entry[0])) {
        continue;
      }
      prioritized.push(entry);
      appended.add(entry[0]);
    }

    return prioritized
      .slice(0, MAX_FORM_COLUMNS)
      .map(([id, value]) => ({ id, name: value.name, nameTh: value.nameTh ?? null }));
  }, [availableFormsByScript, scripts]);

  const columnWidth = "minmax(0, 1fr)";

  const rows = useMemo<ScriptRowMeta[]>(() => {
    return scripts.map((script) => {
      const selectedVersionId = getSelectedVersion(script.id);
      const activeVersionId = selectedVersionId ?? script.current_version_id;

      let lastUpdated: string | null = null;
      let latestEpoch = 0;

      const formsById = new Map<string, ScriptForm>();
      const scoresByFormId = new Map<string, number | null>();

      const filteredForms = script.script_forms.filter((form) => form.script_version_id === activeVersionId);

      filteredForms.forEach((form) => {
        const formId = form.feedback_forms.id;
        formsById.set(formId, form);
        scoresByFormId.set(formId, calculateScriptFormScore(form));

        form.feedback_submissions.forEach((submission) => {
          if (!submission.submitted_at) return;
          const epoch = new Date(submission.submitted_at).getTime();
          if (Number.isNaN(epoch)) return;
          if (epoch > latestEpoch) {
            latestEpoch = epoch;
            lastUpdated = submission.submitted_at;
          }
        });
      });

      if (script.script_versions && script.script_versions.length > 0) {
        script.script_versions.forEach((version) => {
          const versionEpoch = new Date(version.created_at).getTime();
          if (!Number.isNaN(versionEpoch) && versionEpoch > latestEpoch) {
            latestEpoch = versionEpoch;
            lastUpdated = version.created_at;
          }
        });
      }

      return {
        script,
        lastUpdated,
        formsById,
        scoresByFormId,
        activeVersionId,
      } satisfies ScriptRowMeta;
    });
  }, [scripts, getSelectedVersion]);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 260,
    overscan: 6,
    measureElement: (element) => element?.getBoundingClientRect().height ?? 0,
  });

  if (scripts.length === 0) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        {t("dashboard.table.no_scripts")}
      </div>
    );
  }

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalHeight = rowVirtualizer.getTotalSize();
  const versionControlScript = versionControlOpen
    ? scripts.find((s) => s.id === versionControlOpen)
    : null;

  return (
    <div className="space-y-4">
      <div className="mx-auto w-full max-w-7xl rounded-2xl border border-border/80 bg-card/95 backdrop-blur-md shadow-md overflow-hidden">
        <div ref={parentRef} className="relative max-h-[78vh] overflow-y-auto px-4 pt-3 pb-4">
          <ScoreTableHeader columnWidth={columnWidth} formColumns={formColumns} language={language} />
          <div style={{ height: totalHeight, position: "relative" }}>
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index];
              if (!row) return null;

              return (
                <div
                  key={row.script.id}
                  ref={rowVirtualizer.measureElement}
                  data-index={virtualRow.index}
                  className="absolute left-0 w-full"
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  <ScoreTableRow
                    row={row}
                    columnWidth={columnWidth}
                    formColumns={formColumns}
                    availableForms={availableFormsByScript[row.script.id] ?? []}
                    addingForm={addingForm}
                    onAddForm={onAddForm}
                    onShare={onShare}
                    onAnalyze={onAnalyze}
                    language={language}
                    formatDate={formatDate}
                    onVersionChange={(versionId) => {
                      setSelectedVersion(row.script.id, versionId);
                    }}
                    onOpenVersionControl={() => setVersionControlOpen(row.script.id)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {versionControlScript && (
        <VersionControlPanel
          script={versionControlScript}
          open={!!versionControlOpen}
          onClose={() => setVersionControlOpen(null)}
          formatDate={formatDate}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
};
